import type {
  BrandAppHome,
  BrandAppNews,
  BrandAppOfferCard,
  BrandAppNewsScreen,
  BrandAppNewsTarget,
  BrandAppSocialId,
  BrandAppSocials,
  HomeBlockId
} from './brandAppContract'
import { BRAND_APP_SOCIALS, DEFAULT_SOCIALS, isPublishableHomeBlock } from './brandAppContract'
import { isInvalidUrlInput, safeHttpUrl } from './brandAppUrl'

export interface WebappOffer {
  id: number
  offerId: number
  offerPublicId?: string
  rewardId?: number
  image: string | null
  title: string
  description: string | null
  ctaLabel: string | null
  isVisible: boolean
}

export interface WebappNews {
  id: number
  image: string | null
  title: string
  description: string | null
  ctaLabel: string | null
  ctaType: 'external' | 'screen' | null
  ctaUrl: string | null
  ctaScreen: BrandAppNewsScreen | 'survey' | null
  ctaSurveyId?: number | null
  isVisible: boolean
}

export interface WebappMenuStoreUrl {
  storeId: number
  url: string
}

export interface WebappMenu {
  image: string | null
  url: string | null
  storeUrls?: WebappMenuStoreUrl[]
}

export interface WebappHomeFields {
  heroImage?: string | null
  homeBlocks?: string[] | null
  homeBlockTitles?: Record<string, string> | null
}

export interface WebappHomeData extends WebappHomeFields {
  offers: WebappOffer[]
  news: WebappNews[]
  menu: WebappMenu | null
}

const HOME_BLOCK_IDS: HomeBlockId[] = ['offers', 'news', 'menu', 'social']

const isServerId = (id: string) => /^\d+$/.test(id)

export const toOfferCard = (offer: WebappOffer): BrandAppOfferCard => ({
  offerId: offer.offerId,
  publicId: offer.offerPublicId,
  rewardId: offer.rewardId ?? 0,
  image: offer.image ?? '',
  title: offer.title,
  description: offer.description ?? '',
  ctaLabel: offer.ctaLabel ?? '',
  isVisible: offer.isVisible
})

const toNewsTarget = (news: WebappNews): BrandAppNewsTarget => {
  const { ctaType, ctaScreen } = news
  if (ctaType !== 'screen' || !ctaScreen) return { type: 'external', url: news.ctaUrl ?? '' }
  if (ctaScreen === 'survey') return { type: 'screen', screen: 'survey', surveyId: news.ctaSurveyId ?? null }
  return { type: 'screen', screen: ctaScreen }
}

export const toBrandAppNews = (news: WebappNews): BrandAppNews => ({
  id: String(news.id),
  image: news.image ?? '',
  title: news.title,
  description: news.description ?? '',
  ctaLabel: news.ctaLabel ?? '',
  ctaTarget: toNewsTarget(news),
  isVisible: news.isVisible
})

const LEGACY_BLOCK_IDS: Record<string, HomeBlockId> = { offres: 'offers', actus: 'news' }

const toBlockId = (id: string): HomeBlockId | null => {
  const blockId = LEGACY_BLOCK_IDS[id] ?? id
  return HOME_BLOCK_IDS.includes(blockId as HomeBlockId) ? (blockId as HomeBlockId) : null
}

const blockTitlesFromApi = (titles: Record<string, string>): Partial<Record<HomeBlockId, string>> =>
  Object.fromEntries(
    Object.entries(titles).flatMap(([id, title]) => {
      const blockId = toBlockId(id)
      return blockId ? [[blockId, title]] : []
    })
  )

export const homeFromApi = (data: WebappHomeData, base: BrandAppHome): BrandAppHome => {
  const blocks = [...new Set((data.homeBlocks ?? []).flatMap((id) => toBlockId(id) ?? []))]
  const offerCards = Object.fromEntries(data.offers.map((offer) => [offer.offerId, toOfferCard(offer)]))

  return {
    ...base,
    heroImage: data.heroImage ?? '',
    menuImage: data.menu?.image ?? '',
    menuStoreUrls: Object.fromEntries((data.menu?.storeUrls ?? []).map((row) => [row.storeId, row.url])),
    blocks: blocks.length > 0 ? blocks : base.blocks,
    blockTitles: blockTitlesFromApi(data.homeBlockTitles ?? {}),
    offerIds: data.offers.map((offer) => offer.offerId),
    offerCards,
    actus: data.news.map(toBrandAppNews)
  }
}

export const offersPayload = (home: BrandAppHome, serverIds: Map<number, number>) =>
  home.offerIds.flatMap((offerId) => {
    const card = home.offerCards[offerId]
    if (!card) return []

    return [
      {
        id: serverIds.get(offerId),
        offerId,
        image: card.image || null,
        title: card.title,
        description: card.description || null,
        ctaLabel: card.ctaLabel || null,
        isVisible: card.isVisible !== false
      }
    ]
  })

export const newsPayload = (home: BrandAppHome) =>
  home.actus.map((news) => ({
    id: isServerId(news.id) ? Number(news.id) : undefined,
    image: news.image || null,
    title: news.title,
    description: news.description || null,
    ctaLabel: news.ctaLabel || null,
    ctaType: news.ctaTarget.type,
    ctaUrl: news.ctaTarget.type === 'external' ? news.ctaTarget.url.trim() || null : null,
    ctaScreen: news.ctaTarget.type === 'screen' ? news.ctaTarget.screen : null,
    ctaSurveyId:
      news.ctaTarget.type === 'screen' && news.ctaTarget.screen === 'survey' ? news.ctaTarget.surveyId : null,
    isVisible: news.isVisible !== false
  }))

export const menuPayload = (home: BrandAppHome, catalogUrl: string): WebappMenu => ({
  image: home.menuImage || null,
  url: catalogUrl.trim() || null,
  storeUrls: Object.entries(home.menuStoreUrls)
    .map(([storeId, url]) => ({ storeId: Number(storeId), url: url.trim() }))
    .filter((row) => row.url !== '')
})

export const homeFieldsPayload = (home: BrandAppHome) => ({
  heroImage: home.heroImage || null,
  homeBlocks: home.blocks.filter(isPublishableHomeBlock),
  homeBlockTitles: home.blockTitles
})

export interface WebappSocials {
  instagram: string | null
  tiktok: string | null
  facebook: string | null
  youtube: string | null
  website: string | null
  enabled: BrandAppSocialId[]
}

export interface PreviewSocial {
  id: BrandAppSocialId
  url: string
}

const SOCIAL_IDS = BRAND_APP_SOCIALS.map((social) => social.id)

const filledSocials = (socials: BrandAppSocials): BrandAppSocialId[] =>
  SOCIAL_IDS.filter((id) => socials[id].trim() !== '')

export const socialsFromApi = (socials: WebappSocials | null | undefined): BrandAppSocials => {
  if (!socials) return { ...DEFAULT_SOCIALS }

  return {
    instagram: socials.instagram ?? '',
    tiktok: socials.tiktok ?? '',
    facebook: socials.facebook ?? '',
    youtube: socials.youtube ?? '',
    website: socials.website ?? ''
  }
}

export const socialsPayload = (socials: BrandAppSocials): WebappSocials => ({
  instagram: socials.instagram.trim() || null,
  tiktok: socials.tiktok.trim() || null,
  facebook: socials.facebook.trim() || null,
  youtube: socials.youtube.trim() || null,
  website: socials.website.trim() || null,
  enabled: filledSocials(socials)
})

export const hasInvalidSocials = (socials: BrandAppSocials): boolean =>
  SOCIAL_IDS.some((id) => isInvalidUrlInput(socials[id]))

export const previewSocials = (socials: BrandAppSocials): PreviewSocial[] =>
  SOCIAL_IDS.flatMap((id) => {
    const url = safeHttpUrl(socials[id])

    return url ? [{ id, url }] : []
  })
